import { useCallback, useEffect, useLayoutEffect, useMemo, useState, type CSSProperties } from 'react';
import Countdown, { getVotingRemainingSec } from '../components/Countdown';
import IsoCube from '../components/IsoCube';
import Logo from '../components/Logo';
import { apiFetch } from '../lib/api';
import type { ApiError } from '../lib/api';
import { useActiveSessionId } from '../lib/useActiveSession';
import { useGameState } from '../lib/useGameState';
import { useLayoutPreview } from '../lib/layoutPreviewContext';
import type { Participant } from '../lib/types';
import styles from './Vote.module.css';

function VoteStageBackdrop() {
  return (
    <div className={styles.thanksBackdrop} aria-hidden>
      <div className={styles.thanksGridPattern} />
      <div className={styles.thanksGlow} />
    </div>
  );
}

function resolvePublicMediaUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  const base = import.meta.env.BASE_URL;
  const path = url.startsWith('/') ? url.slice(1) : url;
  return `${base}${path}`;
}

export default function Vote() {
  const layoutPreview = useLayoutPreview();
  const { sessionId, loading } = useActiveSessionId();
  const { state, participants } = useGameState(sessionId);
  const [voted, setVoted] = useState<string | null>(() =>
    layoutPreview?.votePhase === 'voted' ? (layoutPreview.participants[0]?.id ?? null) : null,
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const sorted = useMemo<Participant[]>(
    () => [...participants].sort((a, b) => a.slot - b.slot),
    [participants],
  );

  const bySlot = useMemo(() => {
    const m = new Map<number, Participant>();
    for (const p of sorted) m.set(p.slot, p);
    return m;
  }, [sorted]);

  const expected = state?.expected_participants ?? Math.max(sorted.length, 2);
  const oCount = expected <= 2 ? 2 : 4;

  useEffect(() => {
    if (state?.status === 'VOTING') setSelectedId(null);
  }, [state?.status, state?.round]);

  const cast = useCallback(
    async (participantId: string) => {
      if (!sessionId || busy) return;
      if (layoutPreview?.votePhase) {
        setVoted(participantId);
        return;
      }
      setBusy(true);
      setError(null);
      try {
        await apiFetch('/api/vote/cast', {
          method: 'POST',
          body: JSON.stringify({ session_id: sessionId, participant_id: participantId }),
        });
        setVoted(participantId);
      } catch (e) {
        const err = e as ApiError;
        setError(err.message);
        if (err.code === 'ALREADY_VOTED') setVoted(participantId);
      } finally {
        setBusy(false);
      }
    },
    [sessionId, busy, layoutPreview?.votePhase],
  );

  const onPick = useCallback(
    (p: Participant | null) => {
      if (!p || busy) return;
      setSelectedId((cur) => (cur === p.id ? null : p.id));
    },
    [busy],
  );

  const onConfirm = useCallback(() => {
    if (!selectedId || busy) return;
    void cast(selectedId);
  }, [selectedId, busy, cast]);

  const votedParticipant = useMemo(
    () => (voted ? sorted.find((p) => p.id === voted) ?? null : null),
    [sorted, voted],
  );

  const [thanksRemainingSec, setThanksRemainingSec] = useState(0);

  useLayoutEffect(() => {
    if (!voted || !state || state.status !== 'VOTING') {
      setThanksRemainingSec(0);
      return;
    }
    setThanksRemainingSec(getVotingRemainingSec(state.voting_start_time, state.voting_duration_sec));
  }, [voted, state?.status, state?.voting_start_time, state?.voting_duration_sec]);

  useEffect(() => {
    if (!voted || !state || state.status !== 'VOTING') return;
    const tick = () =>
      setThanksRemainingSec(getVotingRemainingSec(state.voting_start_time, state.voting_duration_sec));
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [voted, state?.status, state?.voting_start_time, state?.voting_duration_sec]);

  if (loading) return <div className="appLoading">CONNESSIONE...</div>;
  if (!sessionId) {
    return (
      <div className={styles.shell}>
        <div className={styles.shellInner}>
          <div className="errorBox">Nessuna sessione evento attiva.</div>
        </div>
      </div>
    );
  }

  if (state?.status === 'ENDED') {
    return (
      <div className={`${styles.shell} ${styles.shellVoteClosed}`}>
        <VoteStageBackdrop />
        <div className={styles.shellInnerVoteClosed} role="status" aria-live="polite">
          <div className={styles.voteClosedStack}>
            <div className={styles.voteClosedCopy}>
              <p className={styles.voteClosedEyebrow}>· VOTO PUBBLICO ·</p>
              <h1 className={styles.voteClosedTitle}>
                <span>VOTAZIONE</span>
                <span>CHIUSA</span>
              </h1>
              <p className={styles.voteClosedSub}>Round terminato. Risultati sul maxi schermo.</p>
            </div>
            <div className={styles.voteClosedCubeWrap}>
              <IsoCube uid="vote-closed-cube" className={styles.voteClosedCubeSvg} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (state?.status !== 'VOTING') {
    return (
      <div className={`${styles.shell} ${styles.shellVoteClosed}`}>
        <VoteStageBackdrop />
        <div className={styles.shellInnerVoteClosed} role="status" aria-live="polite">
          <div className={styles.voteClosedStack}>
            <div className={styles.voteClosedCopy}>
              <p className={styles.voteClosedEyebrow}>· VOTO PUBBLICO ·</p>
              <h1 className={styles.voteClosedTitle}>
                <span>VOTO NON</span>
                <span>ANCORA</span>
                <span>APERTO</span>
              </h1>
              <p className={styles.voteClosedSub}>
                Attendi l'apertura delle votazioni.
              </p>
            </div>
            <div className={styles.voteClosedCubeWrap}>
              <IsoCube uid="vote-not-open-cube" className={styles.voteClosedCubeSvg} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (voted) {
    const thanksTimerActive = thanksRemainingSec > 0;

    return (
      <div className={`${styles.shell} ${styles.shellThanksActive}`}>
        <VoteStageBackdrop />
        <div className={styles.shellInnerThanks}>
          <div className={styles.thanksMain}>
            <div className={styles.thanksCheckRing} aria-hidden>
              <svg className={styles.thanksCheckSvg} viewBox="0 0 56 56" fill="none">
                <circle cx="28" cy="28" r="25" stroke="currentColor" strokeWidth="1.5" opacity="0.95" />
                <path
                  d="M17 28.5 L24.5 36 L39 20"
                  stroke="currentColor"
                  strokeWidth="3.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h1 className={styles.thanksHeroTitle}>GRAZIE</h1>
            <p className={styles.thanksLine}>Il tuo voto è stato registrato.</p>
            <p className={styles.thanksLine}>
              {thanksTimerActive
                ? 'Risultati sul maxi schermo tra:'
                : 'Segui i risultati sul maxi schermo.'}
            </p>
            <span aria-hidden={thanksTimerActive ? undefined : true}>
              <Countdown
                className={`${styles.thanksCountdownHero}${thanksTimerActive ? '' : ` ${styles.thanksCountdownExpired}`}`}
                startIso={state.voting_start_time}
                durationSec={state.voting_duration_sec}
              />
            </span>
          </div>
          <p className={styles.thanksVoteFooter}>
            HAI VOTATO · {(votedParticipant?.name ?? '—').toUpperCase()}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.shell} data-slots={oCount}>
      <VoteStageBackdrop />
      <div className={styles.shellInner}>
        <header className={styles.topBar}>
          <Logo size="small" variant="inline" />
          <span className={styles.votingPill} aria-hidden>
            <span className={styles.votingPillDot} />
            VOTING
          </span>
        </header>

        <div className={styles.hero}>
          <h1 className={styles.votaTitle}>VOTA</h1>
          <p className={styles.timerLabel}>Chiusura tra</p>
          <Countdown
            className={styles.timerDigits}
            startIso={state.voting_start_time}
            durationSec={state.voting_duration_sec}
          />
        </div>

        {error ? <div className={styles.inlineError}>{error}</div> : null}

        <div className={styles.gridWrap}>
          <div className={`${styles.grid} ${oCount === 2 ? styles.gridO2 : styles.gridO4}`}>
            {Array.from({ length: oCount }, (_, i) => {
              const slotNum = i + 1;
              const p = bySlot.get(slotNum) ?? null;
              const hasImage = !!(p?.image_url);
              const isSelected = !!(p && selectedId === p.id);
              const disabled = !p || busy;

              return (
                <button
                  key={slotNum}
                  type="button"
                  className={styles.card}
                  style={p ? ({ ['--vote-neon' as string]: p.color } as CSSProperties) : undefined}
                  data-selected={isSelected}
                  data-empty={!p}
                  disabled={disabled}
                  onClick={() => onPick(p)}
                  aria-pressed={p ? isSelected : undefined}
                  aria-label={p ? `Seleziona ${p.name}` : 'Slot vuoto'}
                >
                  {p && isSelected ? (
                    <span className={styles.selectedBadge} aria-hidden>
                      SELECTED
                    </span>
                  ) : null}
                  <div className={styles.cardStripes} aria-hidden />
                  <div className={styles.cardImageArea}>
                    {p && hasImage ? (
                      <img src={resolvePublicMediaUrl(p.image_url!)} alt="" className={styles.cardImg} />
                    ) : p ? (
                      <span className={styles.cardPlaceholder}>Nessuna immagine</span>
                    ) : (
                      <span className={styles.cardPlaceholder}>—</span>
                    )}
                  </div>
                  {p ? (
                    <span className={styles.cardNameCenter}>{p.name.toUpperCase()}</span>
                  ) : null}
                  <div className={styles.cardBar}>
                    {p ? (
                      <>
                        <span className={styles.cardDot} />
                        <span className={styles.cardNameBold}>{p.name.toUpperCase()}</span>
                        <span className={styles.cardSlotNum}>.{String(slotNum).padStart(2, '0')}</span>
                      </>
                    ) : (
                      <span className={styles.cardBarEmpty}>—</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.confirmBar}>
          <button
            type="button"
            className={styles.confirmBtn}
            disabled={!selectedId || busy}
            onClick={onConfirm}
          >
            <span>CONFERMA VOTO</span>
            <span className={styles.confirmArrow} aria-hidden>
              ↑
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
